/****************************************************************************
 * COPYRIGHT (c) 2024 Harman Connected Services
 *
 * All rights reserved
 *
 * This software embodies materials and concepts which are
 * confidential to Ignite Product and is made available solely
 * pursuant to the terms of a written license agreement with
 * Ignite Product.
 *
 * Designed and Developed by Harman International Industries, Inc.
 ***************************************************************************/
import { v4 as uuidv4 } from "uuid";
import { getConfig } from "../../config";
import { DEFAULT_CACHE_TTL, requestCache } from "../../utils/requestCache";

const BASE_URL = getConfig().REACT_APP_FRONTEND_URL;

const getAuthToken = (tokenKey = "access_token") => {
  return sessionStorage.getItem(tokenKey);
};

const generateHeaders = (tokenKey = "access_token", options = {}) => {
  const {
    includeContentType = true,
    includeClientId = true,
    includeAccept = false,
    includeOrigin = false,
  } = options;

  const headers = {};
  
  if (includeContentType) {
    headers["Content-Type"] = "application/json";
  }
  
  if (includeAccept) {
    headers["Accept"] = "application/json";
  }
  
  if (includeOrigin) {
    headers["Access-Control-Allow-Origin"] = "*";
  }

  headers["Authorization"] = `Bearer ${getAuthToken(tokenKey)}`;
  
  if (includeClientId) {
    headers["ClientRequestId"] = uuidv4();
  }

  return headers;
};

const getHeaders = () => generateHeaders("access_token", { includeContentType: true, includeClientId: true });

const multiPartgetHeaders = () => generateHeaders("access_token", { includeContentType: false, includeClientId: true });

const multiPartgetHeadersOEMAdmin = () => generateHeaders("id_token", { includeContentType: false, includeClientId: true });

const getAdminHeaders = () => generateHeaders("id_token", { includeContentType: true, includeAccept: true, includeOrigin: true, includeClientId: false });

const createCacheResolver = (resolve, shouldClearCache) => (payload) => {
  if (shouldClearCache) {
    requestCache.clear();
  }
  resolve(payload);
};

class ApiMethods {
  static apiRequest(method, url, body = {}) {
    url = getConfig().REACT_APP_BACKEND_URL + url;
    return new Promise((resolve, reject) => {
      const shouldClearCache = method !== "GET";
      const resolveWithCache = createCacheResolver(resolve, shouldClearCache);

      fetch(url, {
        method,
        // Original: body: method === "GET" ? null : JSON.stringify(body),
        // Modified to not send body when it's null or empty object
        body: method === "GET" || body === null || body === undefined ? null : JSON.stringify(body),
        headers: getHeaders(),
      })
        .then((res) => {
          if (res.ok) {
            if (res.status === 200) {
              if (res.headers.get("Content-Length") === "0") {
                return res.text().then(resolveWithCache);
              } else {
                return res.json().then(resolveWithCache);
              }
            } else if (res.status !== 204 && res.status !== 200) {
              return res.json().then(resolveWithCache);
            } else {
              return res.text().then(resolveWithCache);
            }
          } else {
            return res.json().then((errorData) => {
              if (Array.isArray(errorData) && errorData.length > 0) {
                reject(Object.assign(new Error(errorData[0]?.message || "An unknown error occured"), { status: res.status }));
              } else {
                reject(Object.assign(new Error(errorData?.message || "An unknown error occured"), { status: res.status }));
              }
            });
          }
        })
        .catch((err) => {
          reject(Object.assign(new Error(err.message || "Network error"), { status: 500 }));
        });
    });
  }

  static apiRequestFormData(method, url, file, onProgress, reportLink = "") {
    const formData = new FormData();
    if (file && file instanceof File) formData.append("file", file);
    console.log("reportLink value in apiRequestFormData:", reportLink);
    if (reportLink && reportLink!=="" && reportLink.trim() !== "") {
      formData.append("extLink", reportLink);
    }
    console.log("Uploading file:", file?.name, "size:", file?.size, "type:", file?.type, "reportLink:", reportLink);
    url = getConfig().REACT_APP_BACKEND_URL + url;
    return new Promise((resolve, reject) => {
      const shouldClearCache = method !== "GET";
      const resolveWithCache = createCacheResolver(resolve, shouldClearCache);

      // Use XMLHttpRequest for progress tracking
      if (onProgress && typeof onProgress === 'function') {
        const xhr = new XMLHttpRequest();
        
        xhr.upload.addEventListener('progress', (e) => {
          if (e.lengthComputable) {
            const percentComplete = Math.round((e.loaded / e.total) * 100);
            onProgress(percentComplete);
          }
        });

        xhr.addEventListener('load', () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            try {
              if (xhr.status === 204) {
                resolveWithCache(xhr.responseText);
              } else {
                const response = JSON.parse(xhr.responseText);
                resolveWithCache(response);
              }
            } catch (err) {
              reject(Object.assign(new Error("Failed to parse response"), { status: xhr.status, cause: err }));
            }
          } else {
            try {
              const errorData = JSON.parse(xhr.responseText);
              if (Array.isArray(errorData) && errorData.length > 0) {
                reject(Object.assign(new Error(errorData[0]?.message || "An unknown error occured"), { status: xhr.status }));
              } else {
                reject(Object.assign(new Error(errorData?.message || "An unknown error occured"), { status: xhr.status }));
              }
            } catch (err) {
              reject(Object.assign(new Error("An unknown error occured"), { status: xhr.status, cause: err }));
            }
          }
        });

        xhr.addEventListener('error', () => {
          reject(Object.assign(new Error("Network error"), { status: 500 }));
        });

        xhr.open(method, url);
        const headers = multiPartgetHeaders();
        Object.keys(headers).forEach(key => {
          xhr.setRequestHeader(key, headers[key]);
        });
        xhr.send(formData);
      } else {
        // Fallback to fetch if no progress callback
        fetch(url, { method, body: formData, headers: multiPartgetHeaders() })
          .then((res) => {
            if (res.ok) {
              if (res.status === 204) {return res.text().then(resolveWithCache);}
              else {return res.json().then(resolveWithCache);}
            } else {
              return res.json().then((errorData) => {
                if (Array.isArray(errorData) && errorData.length > 0) {
                  reject(Object.assign(new Error(errorData[0]?.message || "An unknown error occured"), { status: res.status }));
                } else {
                  reject(Object.assign(new Error(errorData?.message || "An unknown error occured"), { status: res.status }));
                }
              });
            }
          })
          .catch((err) => {
            reject(Object.assign(new Error(err.message || "Network error"), { status: 500 }));
          });
      }
    });
  }

  static apiAPKFileUploadRequest(method, url, body = {}) {
    url = getConfig().REACT_APP_BACKEND_URL + url;

    return new Promise((resolve, reject) => {
      const shouldClearCache = method !== "GET";
      const resolveWithCache = createCacheResolver(resolve, shouldClearCache);

      fetch(url, {
        method: method,
        body: body,
        headers: getHeaders(),
      })
        .then((res) => {
          if (res.ok) {
            if (res.status === 204) {return res.text().then(resolveWithCache);}
            else {return res.json().then(resolveWithCache);}
          } else {
            return res.json().then((errorData) => {
              if (Array.isArray(errorData) && errorData.length > 0) {
                reject(Object.assign(new Error(errorData[0]?.message || "An unknown error occured"), { status: res.status }));
              } else {
                reject(Object.assign(new Error(errorData?.message || "An unknown error occured"), { status: res.status }));
              }
            });
          }
        })
        .catch((err) => {
          reject(Object.assign(new Error(err.message || "Network error"), { status: 500 }));
        });
    });
  }

  static postFormData = (url, formData) => {
    const fullUrl = getConfig().REACT_APP_BACKEND_URL + url;

    const getAuthToken = () => {
      return sessionStorage.getItem("access_token");
    };

    const multiPartHeaders = {
      Authorization: `Bearer ${getAuthToken()}`,
    };

    return new Promise((resolve, reject) => {
      const resolveWithCache = createCacheResolver(resolve, true);

      fetch(fullUrl, {
        method: "POST",
        body: formData,
        headers: multiPartHeaders,
      })
        .then((res) => {
          if (res.ok) {
            if (res.status === 200 || res.status === 201) {
              return res.json().then(resolveWithCache);
            } else if (res.status === 204) {
              return res.text().then(resolveWithCache);
            } else {
              return res.json().then(resolveWithCache);
            }
          } else {
            return res.text().then((text) => {
              let errorData;
              try {
                errorData = JSON.parse(text);
              } catch (e) {
                errorData = { message: text || "An error occurred", cause: e };
              }
              reject(Object.assign(new Error(errorData?.[0]?.message || errorData?.message || `Server returned ${res.status}`), { status: res.status }));
            });
          }
        })
        .catch((err) => {
          reject(Object.assign(new Error(err.message || "Network error"), { status: 500 }));
        });
    });
  };


  static putFormData = (url, formData) => {
    const fullUrl = getConfig().REACT_APP_BACKEND_URL + url;

    const getAuthToken = () => {
      return sessionStorage.getItem("access_token");
    };

    const multiPartHeaders = {
      Authorization: `Bearer ${getAuthToken()}`,
    };

    return new Promise((resolve, reject) => {
      const resolveWithCache = createCacheResolver(resolve, true);

      fetch(fullUrl, {
        method: "PUT",
        body: formData,
        headers: multiPartHeaders,
      })
        .then((res) => {
          if (res.ok) {
            if (res.status === 200 || res.status === 201) {
              return res.json().then(resolveWithCache);
            } else if (res.status === 204) {
              return res.text().then(resolveWithCache);
            } else {
              return res.json().then(resolveWithCache);
            }
          } else {
            return res.text().then((text) => {
              let errorData;
              try {
                errorData = JSON.parse(text);
              } catch (e) {
                errorData = { message: text || "An error occurred", cause: e };
              }
              reject(Object.assign(new Error(errorData?.[0]?.message || errorData?.message || `Server returned ${res.status}`), { status: res.status }));
            });
          }
        })
        .catch((err) => {
          reject(Object.assign(new Error(err.message || "Network error"), { status: 500 }));
        });
    });
  };

  static OEMAdminRequest(method, url, body = {}) {
    const BASE_URL = getConfig().REACT_APP_FRONTEND_URL;
    url = BASE_URL + url;

    const isFormData = body instanceof FormData;

    const headers = isFormData
      ? {
          Accept: "application/json",
          Authorization: `Bearer ${sessionStorage.getItem("id_token")}`,
        }
      : getAdminHeaders();

    return new Promise((resolve, reject) => {
      const shouldClearCache = method !== "GET";
      const resolveWithCache = createCacheResolver(resolve, shouldClearCache);

      let requestBody;
      if (method === "GET") {
        requestBody = null;
      } else if (isFormData) {
        requestBody = body;
      } else {
        requestBody = JSON.stringify(body);
      }

      fetch(url, {
        method,
        body: requestBody,
        headers,
      })
        .then((res) => {
          if (res.ok) {
            if (res.status === 204) {return res.text().then(resolveWithCache);}
            else {return res.json().then(resolveWithCache);}
          } else {
            return res.json().then((errorData) => {
              reject(Object.assign(new Error(errorData?.[0]?.message || errorData?.message || "An unknown error occurred"), { status: res.status }));
            });
          }
        })
        .catch((err) => {
          reject(Object.assign(new Error(err.message || "Network error"), { status: 500 }));
        });
    });
  }

  static OEMAdminRawRequest(method, url, body = null) {
    const BASE_URL = getConfig().REACT_APP_FRONTEND_URL;
    url = BASE_URL + url;

    const headers = getAdminHeaders();

    return new Promise((resolve, reject) => {
      const shouldClearCache = method !== "GET";
      const resolveWithCache = createCacheResolver(resolve, shouldClearCache);

      fetch(url, {
        method,
        body: method === "GET" ? null : body,
        headers,
      })
        .then((res) => {
          if (res.ok) {
            if (res.status === 204) {return res.text().then(resolveWithCache);}
            else {return res.json().then(resolveWithCache);}
          } else {
            return res.json().then((errorData) => {
              reject(Object.assign(new Error(errorData?.[0]?.message || errorData?.message || "An unknown error occurred"), { status: res.status }));
            });
          }
        })
        .catch((err) => {
          reject(Object.assign(new Error(err.message || "Network error"), { status: 500 }));
        });
    });
  }

  static OEMAdminTableauRequest(method, url, body = {}) {
    const BASE_URL = getConfig().REACT_APP_FRONTEND_URL;
    url = BASE_URL + url;

    const tableauHeaders = {
      ...getAdminHeaders(),
      Accept: "application/json, text/plain, */*",
      "Content-Type": "application/json",
    };

    delete tableauHeaders.ClientRequestId;

    return new Promise((resolve, reject) => {
      const shouldClearCache = method !== "GET";
      const resolveWithCache = createCacheResolver(resolve, shouldClearCache);

      fetch(url, {
        method,
        body:
          method === "GET" ||
          (method === "POST" && Object.keys(body).length === 0)
            ? null
            : JSON.stringify(body),
        headers: tableauHeaders,
      })
        .then((res) => {
          if (res.ok) {
            return res.text().then(resolveWithCache);
          } else {
            return res.text().then((errorText) => {
              reject(Object.assign(new Error(errorText || "An unknown error occurred"), { status: res.status }));
            });
          }
        })
        .catch((err) => {
          reject(Object.assign(new Error(err.message || "Network error"), { status: 500 }));
        });
    });
  }

  static GlobalAdminTableauRequest(method, url, body = {}) {
    const BASE_URL = getConfig().REACT_APP_BACKEND_URL;
    url = BASE_URL + url;

    const tableauHeaders = {
      "Content-Type": "application/json;charset=UTF-8",
      Accept: "application/json, text/plain, */*",
      Authorization: `Bearer ${sessionStorage.getItem("id_token")}`,
      ClientRequestId: uuidv4(),
    };

    return new Promise((resolve, reject) => {
      const shouldClearCache = method !== "GET";
      const resolveWithCache = createCacheResolver(resolve, shouldClearCache);

      fetch(url, {
        method,
        body:
          method === "GET" ||
          (method === "POST" && Object.keys(body).length === 0)
            ? null
            : JSON.stringify(body),
        headers: tableauHeaders,
      })
        .then((res) => {
          if (res.ok) {
            return res.text().then(resolveWithCache);
          }
          const rejectWithMessage = (message) => {
            reject(Object.assign(new Error(message || "An unknown error occurred"), { status: res.status }));
          };
          return res
            .json()
            .then((errorData) => {
              rejectWithMessage(errorData?.message);
            })
            .catch(() => res.text().then(rejectWithMessage));
        })
        .catch((err) => {
          reject(Object.assign(new Error(err.message || "Network error"), { status: 500 }));
        });
    });
  }

  static buildCacheKey(scope, url) {
    const token =
      scope === "admin"
        ? sessionStorage.getItem("id_token")
        : sessionStorage.getItem("access_token");
    return `${scope}:${url}:${token || "anon"}`;
  }

  static getUrl(url, options = {}) {
    const { useCache = true, ttl = DEFAULT_CACHE_TTL, cacheKey } = options;
    if (!useCache) {
      return this.apiRequest("GET", url);
    }

    const resolvedKey = cacheKey || this.buildCacheKey("backend", url);
    return requestCache.getOrFetch(
      resolvedKey,
      () => this.apiRequest("GET", url),
      ttl
    );
  }
  static getUrlExcel(url) {
    return this.apiRequestExcel("GET", url);
  }

  static post(url, data) {
    if (data !== undefined && data !== null) return this.apiRequest("POST", url, data);
    else return this.apiRequest("POST", url, null);
  }

  static postAPKFileUpload(url, data) {
    return this.apiAPKFileUploadRequest("POST", url, data);
  }

  static put(url, data) {
    return this.apiRequest("PUT", url, data);
  }

  static delete(url, data) {
    return this.apiRequest("DELETE", url, data);
  }

  static deleteData(url, data) {
    return this.apiRequest("DELETE", url, data);
  }

  static deleteUser(url) {
    return this.apiRequest("DELETE", url);
  }

  static patch(url, data) {
    return this.apiRequest("PATCH", url, data);
  }

  static getAdminUrl(url, options = {}) {
    const { useCache = true, ttl = DEFAULT_CACHE_TTL, cacheKey } = options;
    if (!useCache) {
      return this.OEMAdminRequest("GET", url);
    }

    const resolvedKey = cacheKey || this.buildCacheKey("admin", url);
    return requestCache.getOrFetch(
      resolvedKey,
      () => this.OEMAdminRequest("GET", url),
      ttl
    );
  }
  static postAdminUrl(url, data) {
    return this.OEMAdminRequest("POST", url, data);
  }

  static putAdminUrl(url, data) {
    return this.OEMAdminRequest("PUT", url, data);
  }

  static putAdminUrlRaw(url, data) {
    return this.OEMAdminRawRequest("PUT", url, data);
  }

  static patchAdminUrl(url, data) {
    return this.OEMAdminRequest("PATCH", url, data);
  }

  static deleteAdminUrl(url, data) {
    return this.OEMAdminRequest("DELETE", url);
  }

  static DeletePortfolioSettingURL(url){
    return this.OEMAdminRequest("DELETE", url);
  }

  static postOemAdminTableauUrl(url, data) {
    return this.OEMAdminTableauRequest("POST", url, data);
  }
  static postGlobalAdminTableau(url, data) {
    return this.GlobalAdminTableauRequest("POST", url, data);
  }
  static apiRequestWithoutResponse(method, url, body = {}) {
    url = getConfig().REACT_APP_BACKEND_URL + url;

    return new Promise((resolve, reject) => {
      fetch(url, {
        method,
        body: method === "GET" ? null : JSON.stringify(body),
        headers: getHeaders(),
      })
        .then(() => {
          if (method !== "GET") {
            requestCache.clear();
          }
        })
        .then(resolve)
        .catch(reject);
    });
  }
  static putWithoutResponse(url, data) {
    return this.apiRequestWithoutResponse("PUT", url, data);
  }
  static oemAdminApiRequestFormData(method, url, fileData) {
    url = getConfig().REACT_APP_FRONTEND_URL + url;

    // Extract the actual file from FormData if it's FormData
    let actualFile = fileData;
    if (fileData instanceof FormData) {
      actualFile = fileData.get("file"); // Extract the file from FormData
    }

    // Create new FormData with just the file
    const formData = new FormData();
    formData.append("file", actualFile);

    // Use multipart headers without Content-Type (let browser set it)
    const multipartHeaders = {
      Authorization: `Bearer ${sessionStorage.getItem("id_token")}`,
      ClientRequestId: uuidv4(),
      // Don't set Content-Type - let browser handle multipart boundary
    };

    console.log(
      "Sending file:",
      actualFile.name,
      "size:",
      actualFile.size,
      "type:",
      actualFile.type
    );

    return new Promise((resolve, reject) => {
      const shouldClearCache = method !== "GET";
      const resolveWithCache = createCacheResolver(resolve, shouldClearCache);

      fetch(url, {
        method,
        body: formData,
        headers: multipartHeaders,
      })
        .then((res) => {
          if (res.ok) {
            if (res.status === 204) {return res.text().then(resolveWithCache);}
            else {return res.json().then(resolveWithCache);}
          } else {
            return res.json().then((errorData) => {
              if (Array.isArray(errorData) && errorData.length > 0) {
                reject(Object.assign(new Error(errorData[0]?.message || "An unknown error occured"), { status: res.status }));
              } else {
                reject(Object.assign(new Error(errorData?.message || "An unknown error occured"), { status: res.status }));
              }
            });
          }
        })
        .catch((err) => {
          reject(Object.assign(new Error(err.message || "Network error"), { status: 500 }));
        });
    });
  }
}

export default ApiMethods;
